// Arena camera (lane C, backlog C-1; territory.md §6, visuals §10): locked on the rendered
// (predicted) own head, no lead or lag; the view is a constant world DIAGONAL (WELCOME
// cellsDiagonal) at any aspect; zoom-out by land in ARENA_CLIENT.zoomStep steps with
// easeOutCubic up to WELCOME zoomOutMax (shared `arenaZoomOut`), eased with a 277 ms half-life
// (the reference's τ 400 ms); after death a pan to the killer (half-life deathPanHalfLifeMs);
// spectators at cellsDiagonal × spectatorDiagonalScale over the map centre. No pinch or wheel.
// Acceptance: 50.6 × 28.4 cells at 1366 × 768, 24.3 × 52.7 at 390 × 844.

import { ARENA_CLIENT, arenaZoomOut, type WelcomeArenaInfo } from '@ballio/shared';

export interface ArenaView {
  /** World point at the screen centre (cells). */
  readonly cx: number;
  readonly cy: number;
  /** CSS pixels per cell. */
  readonly scale: number;
  /** Screen size in CSS px. */
  readonly width: number;
  readonly height: number;
}

/** Pixels per cell so the screen diagonal shows `cellsDiagonal` cells. */
export function scaleForDiagonal(widthPx: number, heightPx: number, cellsDiagonal: number): number {
  const d = Math.hypot(Math.max(0, widthPx), Math.max(0, heightPx));
  if (!(d > 0) || !(cellsDiagonal > 0)) return 1;
  return d / cellsDiagonal;
}

/** The fraction of the way to the target after `dtMs` with half-life `halfLifeMs`. */
export function easeFactor(dtMs: number, halfLifeMs: number): number {
  if (!(halfLifeMs > 0)) return 1;
  if (!(dtMs > 0)) return 0;
  return 1 - Math.pow(2, -dtMs / halfLifeMs);
}

/** The reference's τ 400 ms as a half-life (visuals §0: 277 ms). */
export const ZOOM_HALF_LIFE_MS = ARENA_CLIENT.zoomTauMs * Math.LN2;

export type ArenaCameraMode = 'follow' | 'death' | 'spectate';

export class ArenaCamera {
  readonly info: WelcomeArenaInfo;
  mode: ArenaCameraMode = 'spectate';
  /** A fixed view diagonal multiplier instead of the land zoom (the home backdrop). */
  fixedZoom: number | null = null;
  private x: number;
  private y: number;
  /** Current view diagonal multiplier (1 = cellsDiagonal). */
  private zoom = 1;
  private snapped = false;

  constructor(info: WelcomeArenaInfo) {
    this.info = info;
    this.x = info.cols / 2;
    this.y = info.rows / 2;
    this.zoom = ARENA_CLIENT.spectatorDiagonalScale;
  }

  /** The next update jumps to the target (spawn, room change). */
  snap(): void {
    this.snapped = false;
  }

  get position(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /** The view diagonal multiplier the camera is easing toward. */
  targetZoom(landCells: number): number {
    if (this.fixedZoom !== null) return this.fixedZoom;
    if (this.mode === 'spectate') return ARENA_CLIENT.spectatorDiagonalScale;
    return arenaZoomOut(landCells, this.info.playableCells, this.info.zoomOutMax);
  }

  /**
   * One frame. `target` is the own head (follow), the killer (death; null = stay) or ignored
   * (spectate: the map centre, or `target` when given).
   */
  update(
    target: { x: number; y: number } | null,
    landCells: number,
    dtMs: number,
    widthPx: number,
    heightPx: number,
  ): ArenaView {
    const zTarget = this.targetZoom(landCells);
    if (!this.snapped) {
      this.snapped = true;
      this.zoom = zTarget;
      if (target) {
        this.x = target.x;
        this.y = target.y;
      } else if (this.mode === 'spectate') {
        this.x = this.info.cols / 2;
        this.y = this.info.rows / 2;
      }
    } else {
      this.zoom += (zTarget - this.zoom) * easeFactor(dtMs, ZOOM_HALF_LIFE_MS);
      if (this.mode === 'follow' && target) {
        // Hard-locked: the head stays at the screen centre in every frame.
        this.x = target.x;
        this.y = target.y;
      } else {
        const goal =
          target ??
          (this.mode === 'spectate' ? { x: this.info.cols / 2, y: this.info.rows / 2 } : null);
        if (goal) {
          const k = easeFactor(dtMs, ARENA_CLIENT.deathPanHalfLifeMs);
          this.x += (goal.x - this.x) * k;
          this.y += (goal.y - this.y) * k;
        }
      }
    }
    const scale = scaleForDiagonal(widthPx, heightPx, this.info.cellsDiagonal * this.zoom);
    return { cx: this.x, cy: this.y, scale, width: widthPx, height: heightPx };
  }
}

/** World → screen (CSS px). */
export function worldToScreen(v: ArenaView, x: number, y: number): { x: number; y: number } {
  return { x: (x - v.cx) * v.scale + v.width / 2, y: (y - v.cy) * v.scale + v.height / 2 };
}

/** The visible world rectangle (cells), optionally grown by `margin` cells. */
export function viewRect(
  v: ArenaView,
  margin = 0,
): { x0: number; y0: number; x1: number; y1: number } {
  const hw = v.width / 2 / v.scale + margin;
  const hh = v.height / 2 / v.scale + margin;
  return { x0: v.cx - hw, y0: v.cy - hh, x1: v.cx + hw, y1: v.cy + hh };
}
